import { GetContrastColorBW, useTranslation } from "@opendash/core";
import dayjs from "dayjs";
import Parse from "parse";
import React, { useEffect } from "react";
import { useRelativeTime } from "../../../../_shared";
import { Project } from "../../../../js/parsegenerated";
import { ProjectContext } from "../context/ProjectContext";
import { getProjectIcon } from "../functions/getProjectIcon";

/**
 * Returns the project with the given project id or project object or the project from the ProjectContext if no project is given.
 * @param projectProp if given, the project will be returned. if it is a string, the project will be fetched from the server.
 * @returns an object with the project, the color, the icon and the colorText of the project
 * @warning With projectProp as string, the project will be fetched from the server, so it will be undefined at first if not in a context
 */
export const useProject = (projectProp?: string | Project) => {
  const t = useTranslation();
  const project = React.useContext(ProjectContext);
  const [usedProject, setUsedProject] = React.useState(project);
  const getRelativeDateLabel = useRelativeTime();

  useEffect(() => {
    const projectId =
      typeof projectProp === "string" ? projectProp : projectProp?.id;
    if (projectId) {
      const query = new Parse.Query(Project);
      query.get(projectId).then(setUsedProject);
      return;
    }
    if (project) setUsedProject(project);

    if (!project && projectProp === undefined)
      throw new Error(
        "useProject must be used within a ProjectContextProvider or with a project/project id"
      );
  }, [projectProp, project]);

  const start = usedProject?.get("start")
    ? dayjs(usedProject?.get("start"))
    : undefined;
  const end = usedProject?.get("end")
    ? dayjs(usedProject?.get("end"))
    : undefined;
  const color = usedProject?.get("color");
  const icon = getProjectIcon(usedProject);
  const colorText = color ? GetContrastColorBW(color) : undefined;
  const isOverdue = end?.isBefore(dayjs());
  const dateTranslate = (start ? "_start" : "") + (end ? "_end" : "");
  const dateText = t("maintenance:project.date" + dateTranslate, {
    start: getRelativeDateLabel(start, true),
    end: getRelativeDateLabel(end, true),
  });

  return {
    project: usedProject,
    color,
    icon,
    colorText,
    start,
    end,
    isOverdue,
    dateText,
  };
};
